import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Calendar, MapPin, Stethoscope, CalendarDays } from "lucide-react";
import { Toaster } from "sonner";
import { api } from "../lib/api";
import { Page, EmptyState, UrgencyChip, StatusChip, Spinner } from "../components/Brand";
import { timeAgoFr, formatDateFr } from "../lib/fr";

export default function OBGYNInbox() {
  const navigate = useNavigate();
  const { data: refs, isLoading } = useQuery({
    queryKey: ["obgyn-inbox"],
    queryFn: async () => (await api.get("/referrals/inbox")).data,
    refetchInterval: 30000,
  });

  return (
    <Page>
      <Toaster position="top-center" richColors />
      <div className="flex items-end justify-between mb-6 md:mb-8 flex-wrap gap-3">
        <div>
          <div className="cs-label">OB-GYN</div>
          <h1 className="font-semibold mt-1" style={{ fontSize: "32px", letterSpacing: "-0.6px" }}>Boîte de réception</h1>
          <div className="text-[14px] mt-1" style={{ color: "var(--cs-text-muted)" }}>
            Références en attente, triées par urgence puis date.
          </div>
        </div>
        <button onClick={() => navigate("/obgyn/agenda")} className="cs-btn-primary" data-testid="open-agenda-button">
          <CalendarDays size={18} /> Agenda
        </button>
      </div>

      {isLoading ? (
        <div className="cs-card p-10 flex justify-center"><Spinner /></div>
      ) : !refs?.length ? (
        <EmptyState title="Boîte vide" hint="Aucune référence en attente pour le moment." />
      ) : (
        <div className="flex flex-col gap-4" data-testid="obgyn-inbox">
          {refs.map((r) => (
            <button
              key={r.id}
              onClick={() => navigate(`/obgyn/${r.id}`)}
              className="cs-card p-5 text-left hover:-translate-y-0.5 transition-transform"
              data-testid={`inbox-card-${r.referral_code}`}
            >
              <div className="flex items-start justify-between gap-3 pb-3 border-b" style={{ borderColor: "var(--cs-border)" }}>
                <div>
                  <div className="font-semibold text-[17px] tracking-tight">{r.referral_code}</div>
                  <div className="text-[12px] mt-0.5" style={{ color: "var(--cs-text-subtle)" }}>
                    Reçue {timeAgoFr(r.created_at)}
                    {r.created_by_name && <> · {r.created_by_name}</>}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1.5">
                  <UrgencyChip value={r.urgency} />
                  <StatusChip value={r.status} />
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-y-2.5 gap-x-3 pt-3 text-[13px]">
                <Field label="Diagnostic" value={r.diagnosis === "cardiomyopathy" ? "CMP" : "CMP péripartum"} />
                <Field label="FEVG" value={r.ef_range + " %"} />
                <Field label="Âge" value={r.age_range} />
                <Field label="Grossesse" value={pregLabel(r.pregnancy_status)} />
              </div>
              {r.appointment_date && (
                <div className="mt-3 flex items-center gap-2 text-[12px]" style={{ color: "var(--cs-primary-dark)" }}>
                  <Calendar size={14} /> {formatDateFr(r.appointment_date)} · {r.appointment_time}
                  <MapPin size={14} className="ml-2" /> {r.location}
                  <Stethoscope size={14} className="ml-2" /> {r.provider}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </Page>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="cs-label">{label}</div>
      <div className="font-medium mt-0.5">{value}</div>
    </div>
  );
}
function pregLabel(v) {
  return ({ not_pregnant: "Non enceinte", pregnant_t1: "T1", pregnant_t2: "T2", pregnant_t3: "T3", postpartum: "Post-partum" })[v] || v;
}
